/**
 * DensityProvider (TMMAPP-0004). Persists the user's density preference under the storage key
 * `tmm_density`, mirroring `ThemeModeProvider`: storage access is try/catch-guarded (private
 * mode falls back to the default without persisting) and an invalid stored value reads as
 * `comfortable`, the default.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DensityContext, type Density, type DensityContextValue } from './density-context';

export const DENSITY_STORAGE_KEY = 'tmm_density';

function readStoredDensity(): Density {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (stored === 'comfortable' || stored === 'compact') return stored;
  } catch {
    /* localStorage unavailable (e.g. private mode) — fall through to default */
  }
  return 'comfortable';
}

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(readStoredDensity);

  function setDensity(next: Density) {
    setDensityState(next);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      /* localStorage unavailable — preference not persisted */
    }
  }

  const value = useMemo<DensityContextValue>(() => ({ density, setDensity }), [density]);

  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}
